import * as Sentry from '@sentry/react';
import { User } from 'firebase/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';

import { subscribeOnAuthStateChange } from '../services/auth/authService';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider(props: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribeFn = subscribeOnAuthStateChange((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);

      if (currentUser) {
        Sentry.setUser({
          id: currentUser.uid,
          email: currentUser.email ?? undefined,
        });
      } else {
        Sentry.setUser(null);
      }
    });

    return () => unsubscribeFn();
  }, []);

  return <AuthContext.Provider value={{ user, isLoading }}>{props.children}</AuthContext.Provider>;
}
